import { useCallback } from 'react';

import { Orders } from '@/api/Orders';
import {
  ArtworkLink,
  ArtworkMatchFilesResponse,
  AttachmentResponse,
  AuditResponse,
  BookingOperationResponse,
  CampaignMonitorPicturesResponse,
  DecorationRequest,
  DecorationServicesRequest,
  ExtensionRejectRequest,
  ExtensionRequest,
  KeyVisualsResponse,
  Order,
  OrderAppliedFilters,
  OrderBookingResponse,
  OrderFilterResponse,
  OrderPackageBookingResponse,
  OrderRequest,
  OrderResponse,
  OrderStatusResponse,
  PackageExtensionResponse,
  PageResponseOrderResponse,
  PendingExtensionResponse,
  PostgresFilePointer,
  SpaceSupportFileResponse,
  UpdateBooking,
  UpdateBookingPricesRequest,
  UpdateBookingSellingPricesRequest,
  UpdateBookingsServicesRequest,
  UpdateMultipleBookingsAvailability,
  UpdateTaskStatusRequest,
  VisualAllocationResponse,
  VisualAssignmentRequest,
  VisualResourcesRequest,
  VisualResourcesResponse,
} from '@/api/data-contracts';
import { ContentType } from '@/api/http-client';
import { BookingServiceTaskStatusMap } from '@/features/services';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

import { useOrderArtworkPreviewMethods } from './orderArtworkPreviewMethods';
import { createOrderBookingApprovalMethods } from './orderBookingApprovalMethods';
import { useOrderCommentMethods } from './orderCommentMethods';

export interface ExtensionExtras {
  price?: number;
  forceApproved?: boolean;
}

export interface ExtensionPreviewResponse {
  bookingId?: string;
  packageBookingId?: string;
  price: number;
}

const useOrdersService = () => {
  const ordersApi = useUploadProgressService(Orders);

  const commentMethods = useOrderCommentMethods(ordersApi);
  const bookingApprovalMethods = createOrderBookingApprovalMethods(ordersApi);
  const artworkPreviewMethods = useOrderArtworkPreviewMethods(ordersApi);

  const getOrders = async (
    query: {
      request: Omit<OrderRequest, 'sanitizedRequest'>;
    },
    signal: AbortSignal | undefined,
  ): Promise<PageResponseOrderResponse> => {
    const { data } = await ordersApi.listOrders(query.request as OrderRequest, {
      signal,
    });

    return data;
  };

  const getSpaceSupportFile = async (): Promise<SpaceSupportFileResponse> => {
    const { data } = await ordersApi.getOrdersSpaceSupportFile();
    return data;
  };

  const getOrderDetails = async (
    orderId: string,
    signal?: AbortSignal | undefined,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.getOrderById(orderId, { signal });

    return data;
  };

  const getOrderVisualResources = async (
    orderId: string,
    signal?: AbortSignal | undefined,
  ): Promise<VisualResourcesResponse> => {
    const { data } = await ordersApi.getOrderVisualResources(orderId, { signal });
    return data;
  };

  const patchOrderVisualResources = async (
    orderId: string,
    request: VisualResourcesRequest,
    signal?: AbortSignal | undefined,
  ): Promise<VisualResourcesResponse> => {
    const { data } = await ordersApi.patchOrderVisualResources(orderId, request, { signal });
    return data;
  };

  const getOrderStatus = async (
    orderId: string,
    signal?: AbortSignal | undefined,
  ): Promise<OrderStatusResponse> => {
    const { data } = await ordersApi.getOrderStatus(orderId, { signal });

    return data;
  };

  const createOrder = async (planId: string): Promise<OrderResponse> => {
    const { data } = await ordersApi.addOrder(planId);
    return data;
  };

  const deleteOrder = async (
    orderId: string,
    cancelReason?: string,
    additionalInformation?: string,
  ): Promise<OrderResponse> => {
    const cancelData = {
      cancelReason: cancelReason,
      additionalInformation: additionalInformation,
    };

    const { data } = await ordersApi.cancelOrder(orderId, cancelData);
    return data;
  };

  const confirmOrder = async (orderId: string): Promise<void> => {
    await ordersApi.confirmOrder(orderId);
  };

  const downloadOrderDocumentById = useCallback(
    async (orderId: string, documentId: string, signal?: AbortSignal) => {
      const { data } = await ordersApi.downloadOrderDocument(orderId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const downloadOrderDocumentImageById = useCallback(
    async (orderId: string, documentId: string, signal?: AbortSignal) => {
      const { data } = await ordersApi.getLayoutSketchToImage(orderId, documentId, {
        format: 'blob',
        signal,
      });
      return data;
    },
    [ordersApi],
  );

  const getBookingGeometry = useCallback(
    async (orderId: string, bookingId: string): Promise<string | object | null> => {
      const { data } = await ordersApi.getBookingGeometry(orderId, bookingId);
      return data ?? null;
    },
    [ordersApi],
  );

  const updateBookingGeometry = useCallback(
    async (orderId: string, bookingId: string, geometry: string): Promise<void> => {
      await ordersApi.updateBookingGeometry(orderId, bookingId, { geometry });
    },
    [ordersApi],
  );

  const updateOrderDocuments = async (
    orderId: string,
    keyVisuals: PostgresFilePointer[],
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updateOrderDocuments(
      orderId,
      { keyVisuals },
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  };

  const updateBooking = async (
    orderId: string,
    bookingId: string,
    form: UpdateBooking,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updateBooking(orderId, bookingId, form);
    return data;
  };

  const updatePackageBooking = async (
    orderId: string,
    bookingId: string,
    form: UpdateBooking,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updatePackageBooking(orderId, bookingId, form);
    return data;
  };

  const updateMultipleBookingsAvailability = async (
    orderId: string,
    payload: UpdateMultipleBookingsAvailability,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updateOrderBookingsAvailability(orderId, payload);
    return data;
  };

  const createBookingExtension = async (
    orderId: string,
    bookingId: string,
    form: ExtensionExtras & ExtensionRequest,
  ): Promise<BookingOperationResponse> => {
    const { data } = await ordersApi.createExtension1(orderId, bookingId, form);
    return data;
  };

  const previewBookingExtensionPrice = async (
    orderId: string,
    bookingId: string,
    endDate: string,
    signal?: AbortSignal,
  ): Promise<ExtensionPreviewResponse> => {
    const { data } = await ordersApi.previewBookingExtensionCost(
      orderId,
      bookingId,
      { newEndDate: endDate },
      { signal },
    );
    return { bookingId, price: data.priceDelta };
  };

  const previewPackageBookingExtensionPrice = async (
    orderId: string,
    packageBookingId: string,
    endDate: string,
    signal?: AbortSignal,
  ): Promise<ExtensionPreviewResponse> => {
    const { data } = await ordersApi.previewPackageBookingExtensionCost(
      orderId,
      packageBookingId,
      { newEndDate: endDate },
      { signal },
    );
    return { packageBookingId, price: data.priceDelta };
  };

  const submitBookingExtension = async (
    orderId: string,
    bookingId: string,
    operationId: string,
  ): Promise<BookingOperationResponse> => {
    const { data } = await ordersApi.submitExtension1(orderId, bookingId, operationId);
    return data;
  };

  // `addDecoration`/`cancelDecoration` are the BOOKING routes
  // (`/bookings/{bookingId}/decorations`) only while no package decoration controller exists: the
  // generated names track declaration order, so a regeneration against an environment that still
  // serves one would push these to `1` and silently retarget the write — the signatures are
  // identical. Verify the route in `src/api/Orders.ts`, never the name.
  const addBookingDecoration = async (
    orderId: string,
    bookingId: string,
    form: DecorationRequest,
  ): Promise<BookingOperationResponse> => {
    const { data } = await ordersApi.addDecoration(orderId, bookingId, form);
    return data;
  };

  const cancelBookingDecoration = async (
    orderId: string,
    bookingId: string,
    operationId: string,
  ): Promise<BookingOperationResponse> => {
    const { data } = await ordersApi.cancelDecoration(orderId, bookingId, operationId);
    return data;
  };

  const updateBookingDecorationServices = async (
    orderId: string,
    bookingId: string,
    operationId: string,
    form: DecorationServicesRequest,
  ): Promise<BookingOperationResponse> => {
    const { data } = await ordersApi.updateDecorationServices(
      orderId,
      bookingId,
      operationId,
      form,
    );
    return data;
  };

  const getBookingOperations = async (
    orderId: string,
    bookingId: string,
    signal?: AbortSignal,
  ): Promise<BookingOperationResponse[]> => {
    const { data } = await ordersApi.getTimeline(orderId, bookingId, { signal });
    return data;
  };

  const createPackageBookingExtension = async (
    orderId: string,
    packageBookingId: string,
    form: ExtensionExtras & ExtensionRequest,
  ): Promise<PackageExtensionResponse> => {
    const { data } = await ordersApi.createExtension(orderId, packageBookingId, form);
    return data;
  };

  const submitPackageBookingExtension = async (
    orderId: string,
    packageBookingId: string,
  ): Promise<PackageExtensionResponse> => {
    const { data } = await ordersApi.submitExtension(orderId, packageBookingId);
    return data;
  };

  const approveBookingExtension = async (
    orderId: string,
    bookingId: string,
    operationId: string,
  ): Promise<BookingOperationResponse> => {
    const { data } = await ordersApi.approveExtension1(orderId, bookingId, operationId);
    return data;
  };

  const rejectBookingExtension = async (
    orderId: string,
    bookingId: string,
    operationId: string,
    form: ExtensionRejectRequest,
  ): Promise<BookingOperationResponse> => {
    const { data } = await ordersApi.rejectExtension1(orderId, bookingId, operationId, form);
    return data;
  };

  const approvePackageBookingExtension = async (
    orderId: string,
    packageBookingId: string,
  ): Promise<PackageExtensionResponse> => {
    const { data } = await ordersApi.approveExtension(orderId, packageBookingId);
    return data;
  };

  const rejectPackageBookingExtension = async (
    orderId: string,
    packageBookingId: string,
    form: ExtensionRejectRequest,
  ): Promise<PackageExtensionResponse> => {
    const { data } = await ordersApi.rejectExtension(orderId, packageBookingId, form);
    return data;
  };

  const getPendingExtensions = async (
    orderId: string,
    signal?: AbortSignal,
  ): Promise<PendingExtensionResponse[]> => {
    const { data } = await ordersApi.getPendingExtensions(orderId, { signal });
    return data;
  };

  const deleteOrderDocument = async (
    orderId: string,
    documentId: string,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.deleteOrderDocument(orderId, documentId);
    return data;
  };

  const deleteOrderArtwork = async (
    orderId: string,
    bookingId: string,
    documentId: string,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.deleteOrderArtwork(orderId, bookingId, documentId);
    return data;
  };

  const getFilterValues = async (
    signal?: AbortSignal | undefined,
  ): Promise<OrderFilterResponse> => {
    const { data } = await ordersApi.getOrderFilterValues({ signal });
    return data;
  };

  const getOrderFilters = async (
    orderId: string,
    signal?: AbortSignal | undefined,
  ): Promise<OrderAppliedFilters> => {
    const { data } = await ordersApi.getOrderFilters(orderId, { signal });
    return data;
  };

  const updateOrderFilters = async (
    orderId: string,
    request: OrderAppliedFilters,
    signal?: AbortSignal | undefined,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updateOrderFilters(orderId, request, { signal });
    return data;
  };

  const uploadOrderAttachmentVersion = useCallback(
    async (
      orderId: string,
      attachmentId: string,
      category: string,
      formData: FormData,
    ): Promise<AttachmentResponse> => {
      const { data } = await ordersApi.updateNewVersion1(orderId, category, attachmentId, {}, {
        body: formData,
        type: ContentType.FormData,
      } as any);
      return data;
    },
    [ordersApi],
  );

  const uploadArtworkPreviewNewVersion = useCallback(
    async (
      orderId: string,
      bookingId: string,
      attachmentId: string,
      formData: FormData,
    ): Promise<AttachmentResponse> => {
      const { data } = await ordersApi.updateArtworkPreviewVersion(
        orderId,
        bookingId,
        attachmentId,
        formData as any,
      );
      return data;
    },
    [ordersApi],
  );

  const setAttachmentActiveVersion = useCallback(
    async (
      orderId: string,
      attachmentId: string,
      versionId: number,
    ): Promise<AttachmentResponse> => {
      const { data } = await ordersApi.setActiveVersion1(orderId, attachmentId, versionId);
      return data;
    },
    [ordersApi],
  );

  const uploadNewArtworkPreview = useCallback(
    async (
      orderId: string,
      bookingId: string,
      formData: FormData,
      operationId?: string,
    ): Promise<OrderResponse> => {
      const { data } = await ordersApi.uploadOrderArtworkPreview(
        orderId,
        bookingId,
        formData as any,
        operationId ? { operationId } : {},
      );
      return data;
    },
    [ordersApi],
  );

  const deleteAttachmentVersion = useCallback(
    async (
      orderId: string,
      attachmentId: string,
      versionId: number,
    ): Promise<AttachmentResponse> => {
      const { data } = await ordersApi.deleteVersion1(orderId, attachmentId, versionId);
      return data;
    },
    [ordersApi],
  );

  const getOrderKeyVisuals = useCallback(
    async (orderId: string, signal?: AbortSignal | undefined): Promise<KeyVisualsResponse> => {
      const { data } = await ordersApi.getOrderKeyVisuals(orderId, { signal });
      return data;
    },
    [ordersApi],
  );

  const updateStatusForOrderKeyVisuals = useCallback(
    async (
      orderId: string,
      taskId: string,
      request: UpdateTaskStatusRequest,
    ): Promise<KeyVisualsResponse> => {
      const { data } = await ordersApi.updateStatusForOrderKeyVisuals(orderId, taskId, request);
      return data;
    },
    [ordersApi],
  );

  const locationAddOrderBookings = useCallback(
    async (spaceId: string, request: Order) => {
      const { data } = await ordersApi.locationAddOrderBookings(spaceId, request);
      return data;
    },
    [ordersApi],
  );

  const getOrderPackageBookingsForPackage = async (
    advertisingPackageId: string,
    signal?: AbortSignal | undefined,
  ): Promise<OrderPackageBookingResponse[]> => {
    const { data } = await ordersApi.getOrderPackageBookingsForPackage(advertisingPackageId, {
      signal,
    });
    return data;
  };

  const updateInternalBookingPrices = async (
    orderId: string,
    payload: UpdateBookingPricesRequest,
  ) => {
    return ordersApi.updateInternalBookingPrices(orderId, payload);
  };

  const previewInternalBookingPrices = async (
    orderId: string,
    payload: UpdateBookingPricesRequest,
  ) => {
    const { data } = await ordersApi.previewInternalBookingPrices(orderId, payload);
    return data;
  };

  const updateBookingSellingPrices = async (
    orderId: string,
    payload: UpdateBookingSellingPricesRequest,
  ) => {
    return ordersApi.updateBookingSellingPrices(orderId, payload);
  };

  const previewBookingSellingPrices = async (
    orderId: string,
    payload: UpdateBookingSellingPricesRequest,
  ) => {
    const { data } = await ordersApi.previewBookingSellingPrices(orderId, payload);
    return data;
  };

  const updateBookingArtworkLink = async (
    orderId: string,
    bookingId: string,
    payload: ArtworkLink[],
    operationId?: string,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updateBookingArtWorkLink(
      orderId,
      bookingId,
      payload,
      operationId ? { operationId } : {},
    );
    return data;
  };

  const updateBookingServices = async (
    orderId: string,
    payload: UpdateBookingsServicesRequest,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updateBookingsServices(orderId, payload);
    return data;
  };

  const uploadBookingArtworkMatchFiles = useCallback(
    async (
      orderId: string,
      bookingId: string,
      files: File[],
    ): Promise<ArtworkMatchFilesResponse> => {
      const formData = new FormData();
      files.forEach((file) => formData.append('artworkMatchFiles', file));
      const { data } = await ordersApi.uploadBookingArtworkMatchFiles(
        orderId,
        bookingId,
        formData as any,
      );
      return data;
    },
    [ordersApi],
  );

  const deleteBookingArtworkMatchFile = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string,
    ): Promise<ArtworkMatchFilesResponse> => {
      const { data } = await ordersApi.deleteBookingArtworkMatchFile(
        orderId,
        bookingId,
        documentId,
      );
      return data;
    },
    [ordersApi],
  );

  const downloadBookingArtworkMatchFile = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const response = await ordersApi.downloadArtworkMatchFile1(orderId, bookingId, documentId, {
        format: 'blob',
        signal,
      } as any);
      return response.data;
    },
    [ordersApi],
  );

  const downloadBookingArtworkMatchThumbnail = useCallback(
    async (orderId: string, bookingId: string, documentId: string): Promise<Blob> => {
      const response = await ordersApi.getBookingArtworkMatchThumbnail(
        orderId,
        bookingId,
        documentId,
      );
      return response.data;
    },
    [ordersApi],
  );

  const assignBookingOperationVisual = useCallback(
    async (
      orderId: string,
      bookingId: string,
      operationId: string,
      request: VisualAssignmentRequest,
    ): Promise<VisualAllocationResponse[]> => {
      const { data } = await ordersApi.assignVisual(orderId, bookingId, operationId, request);
      return data;
    },
    [ordersApi],
  );

  const getOrderHistory = useCallback(
    async (orderId: string): Promise<AuditResponse[]> => {
      const { data } = await ordersApi.getOrderHistory(orderId);
      return data;
    },
    [ordersApi],
  );

  const getOrderBookingsForSpace = useCallback(
    async (spaceId: string, signal?: AbortSignal | undefined): Promise<OrderBookingResponse[]> => {
      const { data } = await ordersApi.getOrderBookingsForSpace(spaceId, { signal });
      return data;
    },
    [ordersApi],
  );

  const uploadBookingCampaignMonitorFiles = useCallback(
    async (
      orderId: string,
      bookingId: string,
      files: File[],
      fieldKey:
        | 'beforeDecorationFiles'
        | 'afterDecorationFiles'
        | 'beforeNeutralizationFiles'
        | 'afterNeutralizationFiles',
      operationId?: string,
    ): Promise<CampaignMonitorPicturesResponse> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append(fieldKey, file);
      });
      if (operationId) {
        // The photos land on this decoration. The generated method has no query slot for it yet
        // and gains one in a different position once regenerated, so the call names the query itself.
        const { data } = await ordersApi.request<CampaignMonitorPicturesResponse, unknown>({
          path: `/orders/${orderId}/bookings/${bookingId}/campaignMonitor`,
          method: 'POST',
          query: { operationId },
          body: formData,
          secure: true,
          type: ContentType.FormData,
        });
        return data;
      }
      const { data } = await ordersApi.uploadBookingCampaignMonitorFiles(
        orderId,
        bookingId,
        formData as any,
      );
      return data;
    },
    [ordersApi],
  );

  const deleteBookingCampaignMonitorFile = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string,
    ): Promise<CampaignMonitorPicturesResponse> => {
      const { data } = await ordersApi.deleteBookingCampaignMonitorFile(
        orderId,
        bookingId,
        documentId,
      );
      return data;
    },
    [ordersApi],
  );

  const downloadBookingCampaignMonitorFile = useCallback(
    async (
      orderId: string,
      bookingId: string,
      documentId: string,
      signal?: AbortSignal,
    ): Promise<Blob> => {
      const response = await ordersApi.downloadBookingCampaignMonitorFile(
        orderId,
        bookingId,
        documentId,
        { format: 'blob', signal } as any,
      );
      return response.data;
    },
    [ordersApi],
  );

  const downloadBookingCampaignMonitorThumbnail = useCallback(
    async (orderId: string, bookingId: string, documentId: string): Promise<Blob> => {
      const response = await ordersApi.getBookingCampaignMonitorThumbnail(
        orderId,
        bookingId,
        documentId,
      );
      return response.data;
    },
    [ordersApi],
  );

  const getOrderServiceTasksStatus = useCallback(
    async (orderId: string, signal?: AbortSignal): Promise<BookingServiceTaskStatusMap> => {
      const { data } = await ordersApi.getOrderServiceTasksStatus(orderId, { signal });
      return data;
    },
    [ordersApi],
  );

  const updateResponsibleUsers = async (
    orderId: string,
    responsibleUsers: Record<string, string>,
  ): Promise<OrderResponse> => {
    const { data } = await ordersApi.updateResponsibleUsers(orderId, responsibleUsers);
    return data;
  };

  return {
    ...commentMethods,
    ...artworkPreviewMethods,
    ...bookingApprovalMethods,
    getOrders,
    getOrderDetails,
    createOrder,
    deleteOrder,
    confirmOrder,
    downloadOrderDocumentById,
    downloadOrderDocumentImageById,
    updateOrderDocuments,
    updateBooking,
    updatePackageBooking,
    deleteOrderDocument,
    getFilterValues,
    uploadOrderAttachmentVersion,
    uploadArtworkPreviewNewVersion,
    uploadNewArtworkPreview,
    setAttachmentActiveVersion,
    deleteAttachmentVersion,
    getOrderKeyVisuals,
    updateStatusForOrderKeyVisuals,
    locationAddOrderBookings,
    getOrderPackageBookingsForPackage,
    getOrderFilters,
    updateOrderFilters,
    getOrderStatus,
    updateInternalBookingPrices,
    previewInternalBookingPrices,
    updateBookingSellingPrices,
    previewBookingSellingPrices,
    getOrderVisualResources,
    patchOrderVisualResources,
    deleteOrderArtwork,
    updateBookingArtworkLink,
    getSpaceSupportFile,
    updateMultipleBookingsAvailability,
    createBookingExtension,
    previewBookingExtensionPrice,
    previewPackageBookingExtensionPrice,
    submitBookingExtension,
    addBookingDecoration,
    cancelBookingDecoration,
    updateBookingDecorationServices,
    getBookingOperations,
    createPackageBookingExtension,
    submitPackageBookingExtension,
    approveBookingExtension,
    rejectBookingExtension,
    approvePackageBookingExtension,
    rejectPackageBookingExtension,
    getPendingExtensions,
    updateBookingServices,
    uploadBookingArtworkMatchFiles,
    deleteBookingArtworkMatchFile,
    downloadBookingArtworkMatchFile,
    downloadBookingArtworkMatchThumbnail,
    assignBookingOperationVisual,
    getOrderHistory,
    uploadBookingCampaignMonitorFiles,
    deleteBookingCampaignMonitorFile,
    downloadBookingCampaignMonitorFile,
    downloadBookingCampaignMonitorThumbnail,
    getBookingGeometry,
    updateBookingGeometry,
    getOrderServiceTasksStatus,
    updateResponsibleUsers,
    getOrderBookingsForSpace,
  };
};

export default useOrdersService;
